/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import { PriceComponent } from '@msdyn365-commerce/components';
import { ChannelIdentity, Customer, OrderOriginator, OrderShipments, OrgUnitLocation, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import * as React from 'react';
import { countItems, IModuleComponentProps, IPriceContext } from './utilities';
import { isOboRequest } from '@msdyn365-commerce/core';

export interface IOrderInformationInputResources {
    receiptEmailMessage?: string;
    orderItemLabel: string;
    orderItemsLabel: string;
    processingLabel: string;
    orderIdLabel: string;
    confirmationIdLabel: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    orderPlacedByFullText?: string;
    orderPlacedByYouText?: string;
    orderOnBehalfOfText?: string;
}
export interface IOrderInformationInput {
    moduleName: string;
    channelReferenceId?: string;
    receiptEmail?: string;
    createdDate?: string;
    amount?: number;
    priceContext: IPriceContext;
    salesId?: string;
    hasTrackingInfo?: boolean;
    count?: number;
    resources: IOrderInformationInputResources;
    originator?: OrderOriginator;
    isYou?: [boolean, boolean];
    isB2bAdmin?: boolean;
    isOnlineStore?: boolean;
    priceCurrency?: string;
}
const callCenterChannelTypeValue = 4;
export interface IOrderInformation {
    orderInformationProps: INodeProps;
    placedBy?: React.ReactNode;
    salesId: React.ReactNode;
    receiptEmail: React.ReactNode;
    createdDate: React.ReactNode;
    count: React.ReactNode;
    amount: React.ReactNode;
    channelReferenceId: React.ReactNode;
}

const getPlacedBy = (
    moduleName: string,
    originator: OrderOriginator | undefined,
    isYou: [boolean, boolean] | undefined,
    orderPlacedByFullText: string | undefined,
    orderPlacedByYouText: string | undefined,
    orderOnBehalfOfText: string | undefined
): React.ReactNode | undefined => {
    let placedByNode: React.ReactNode | undefined;
    if (originator) {
        const placedByName =
            originator.StaffId && originator.StaffName
                ? originator.StaffName
                : originator.CustomerId && originator.CustomerName
                ? originator.CustomerName
                : '-';
        const onBehalfOfText = originator.StaffId && originator.StaffName && originator.CustomerId && originator.CustomerName;
        placedByNode = (
            <div className={`${moduleName}__sales-order-placed-by`}>
                {orderPlacedByFullText} <span className='bolded'>{placedByName}</span> {isYou && isYou[0] && ` ${orderPlacedByYouText}`}
                {onBehalfOfText && (
                    <div>
                        {orderOnBehalfOfText} <span className='bolded'>{`${originator.CustomerName}`}</span>
                        {isYou && isYou[1] && ` ${orderPlacedByYouText}`}
                    </div>
                )}
            </div>
        );
    }
    return placedByNode;
};

export const getOrderInformation = ({
    moduleName,
    channelReferenceId,
    receiptEmail,
    createdDate,
    amount,
    priceContext,
    originator,
    isYou,
    salesId,
    count,
    priceCurrency,
    resources: {
        receiptEmailMessage,
        orderItemLabel,
        orderItemsLabel,
        processingLabel,
        orderIdLabel,
        orderPlacedByFullText,
        orderPlacedByYouText,
        orderOnBehalfOfText,
        confirmationIdLabel
    }
}: IOrderInformationInput): IOrderInformation => ({
    orderInformationProps: { className: `${moduleName}__order-information` },
    placedBy: getPlacedBy(moduleName, originator, isYou, orderPlacedByFullText, orderPlacedByYouText, orderOnBehalfOfText),
    salesId: <span className={`${moduleName}__order-information-sales-id`}>{`${orderIdLabel} ${salesId || processingLabel}`}</span>,
    receiptEmail: receiptEmail && (
        <span className={`${moduleName}__order-information-receipt-email`}>
            {receiptEmailMessage}
            {receiptEmail}
        </span>
    ),
    createdDate: <span className={`${moduleName}__order-information-created-date`}>{createdDate}</span>,
    count: count !== undefined && count > 0 && (
        <span className={`${moduleName}__order-information-count`}>
            {count === 1 ? `${count} ${orderItemLabel}` : `${count} ${orderItemsLabel}`}
        </span>
    ),
    amount: (amount || amount === 0) && (
        <PriceComponent
            {...priceContext}
            className={`${moduleName}__order-information-amount`}
            data={{ price: { CustomerContextualPrice: amount } }}
            freePriceText={`£${Number(0).toFixed(2)}`}
            currencyCode={priceCurrency}
        />
    ),
    channelReferenceId: channelReferenceId && (
        <span className={`${moduleName}__order-information-channel-reference-id`}>{`${confirmationIdLabel} ${channelReferenceId}`}</span>
    )
});

interface IProps extends IModuleComponentProps {
    resources: IOrderInformationInputResources;
    customer?: Customer;
}

/**
 * Retrieves channel is online store or not.
 * @param channelTypeValue - The channel type.
 * @param channelAddress - Channel Address.
 * @returns {Boolean} that indicates that the channel is in the online store.
 */
export const isOnlineStoreChannel = (channelTypeValue: number | undefined, channelAddress: string | undefined): boolean => {
    return channelTypeValue !== callCenterChannelTypeValue && !channelAddress;
};
export const getOrderChannelTypeValue: (order: SalesOrder | OrderShipments, channelIdentities?: ChannelIdentity[]) => number | undefined = (
    order: SalesOrder | OrderShipments,
    channelIdentities?: ChannelIdentity[]
) => {
    const orderChannel = channelIdentities?.find(channel => {
        return channel.RecordId === order.ChannelId;
    });

    const channelType = orderChannel?.ChannelType.Value;
    return channelType;
};

/**
 * Retrieves order channel address.
 * @param order - Order info.
 * @param orgUnitLocations - OrgUnitLocation data.
 * @returns String with the address of the channel or an empty string.
 */
export const getOrderChannelAddress: (order: SalesOrder | OrderShipments, orgUnitLocations?: OrgUnitLocation[]) => string = (
    order: SalesOrder | OrderShipments,
    orgUnitLocations?: OrgUnitLocation[]
) => {
    const orderOrgUnitLocation = orgUnitLocations?.find(location => {
        return location.ChannelId === order.ChannelId;
    });

    const channelAddress = orderOrgUnitLocation?.Address ?? '';
    return channelAddress;
};

/**
 * @param {Customer} customer A current customer.
 * @param {OrderOriginator} originator The order originator.
 *  */
export const getOrderInfomationInput = (
    props: IProps,
    moduleName: string,
    order: SalesOrder | OrderShipments,
    priceContext: IPriceContext,
    channelIdentities?: ChannelIdentity[],
    orgUnitLocations?: OrgUnitLocation[],
    customer?: Customer,
    originator?: OrderOriginator,
    showChannelInfo?: boolean,
    isSalesInvoice?: boolean,
    shouldShowQrCode?: boolean
): IOrderInformationInput => {
    const {
        resources,
        context: {
            request: {
                apiSettings: { channelId }
            }
        }
    } = props;

    // @ts-ignore OrderShipments doesnt has ChannelId
    const showTrackingInfo = !!order.ChannelId && order.ChannelId === channelId;

    const locale = get(props, 'context.request.locale') || 'en-US';
    const count = order.SalesLines && order.SalesLines.length ? countItems(order.SalesLines) : 0;
    const channelTypeValue = getOrderChannelTypeValue(order, channelIdentities);
    const channelAddress = getOrderChannelAddress(order, orgUnitLocations);
    const isYou =
        !isOboRequest(props.context.request) &&
        originator?.CustomerId &&
        originator?.CustomerName &&
        !originator?.StaffId &&
        !originator?.StaffName &&
        customer?.AccountNumber === originator?.CustomerId
            ? true
            : false;
    const isYouObo = !isYou && !isOboRequest(props.context.request);
    return {
        moduleName: moduleName,
        createdDate: (order.CreatedDateTime && new Date(order.CreatedDateTime).toLocaleDateString(locale)) || '',
        salesId: order.SalesId,
        priceCurrency: order.CurrencyCode,
        originator: originator,
        isYou: [isYou, isYouObo],
        isB2bAdmin: customer?.IsB2bAdmin,
        // @ts-ignore OrderShipments doesnt has ReceiptEmail
        receiptEmail: order.ReceiptEmail,
        channelReferenceId: order.ChannelReferenceId,
        amount: order.TotalAmount || 0,
        priceContext: priceContext,
        count,
        resources,
        isOnlineStore: isOnlineStoreChannel(channelTypeValue, channelAddress)
    };
};
